import { getToken } from "./user-service";

// Passenger requests desk (role PAX_REQUESTS_DESK).
// Every call carries the signed-in user's real Firebase ID token; access is
// enforced server-side.
const base_url = "/api/v1/bookings/pax-desk/requests";
const legacy_url = "/api/v1/bookings/pax-desk/legacy-requests";
const airline_url = "/api/v1/bookings/airline-pax-desk/requests";

// airline-pax-desk accounts hit the carrier-scoped routes; everyone else the PRM desk routes.
// Same request/response shape, so the page and row components are shared.
const requestsBase = (airline) => (airline ? airline_url : base_url);

// error carrying the HTTP status so the desk can sign out on 401/403,
// show the server message on 400/404/409 and back off on 429
const deskError = (status, message) => {
  const error = new Error(message || "Something went wrong, please try again.");
  error.status = status;
  return error;
};

const readJson = async (response) => {
  try {
    return await response.json();
  } catch (e) {
    return null;
  }
};

const request = async (url, options = {}) => {
  let token;
  try {
    token = await getToken();
  } catch (e) {
    // no signed-in user — treat as unauthorised
    throw deskError(401, "Your session has ended. Please log in again.");
  }

  let response;
  try {
    response = await fetch(url, {
      ...options,
      cache: "no-store",
      headers: {
        accept: "application/json",
        "Content-Type": "application/json",
        "Firebase-UID": token,
      },
    });
  } catch (e) {
    throw deskError(0, "Could not reach the server. Check your connection and try again.");
  }

  const json = await readJson(response);

  if (!response.ok) {
    let message = json && typeof json.message === "string" ? json.message : null;
    if (response.status === 429) {
      message = "Too many requests, wait a moment.";
    }
    throw deskError(response.status, message);
  }

  return json;
};

const listQuery = ({ view, from, to, q, limit, skip, sort } = {}) => {
  const params = new URLSearchParams();
  if (view) params.set("view", view);
  if (sort) params.set("sort", sort);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  if (q) params.set("q", String(q).slice(0, 60));
  if (limit != null) params.set("limit", String(limit));
  if (skip != null) params.set("skip", String(skip));
  const query = params.toString();
  return query ? "?" + query : "";
};

const listResult = (json, from, to) => ({
  requests: Array.isArray(json?.requests) ? json.requests : [],
  total: typeof json?.total === "number" ? json.total : 0,
  from: json?.from || from || null,
  to: json?.to || to || null,
});

// GET requests for the desk
// view: 'open' | 'confirmed' | 'rejected', sort: 'upcoming' | 'newest'
// from/to: 'YYYY-MM-DD', q: search (<= 60 chars)
// -> { requests: DeskRequest[], total, from, to }
export const getPaxDeskRequests = async (filters = {}, { airline = false } = {}) => {
  const json = await request(requestsBase(airline) + listQuery(filters), { method: "GET" });
  return listResult(json, filters.from, filters.to);
};

// GET legacy-app bookings (read-only), same filters and shape as getPaxDeskRequests
export const getLegacyAppBookings = async (filters = {}) => {
  const json = await request(legacy_url + listQuery(filters), { method: "GET" });
  return listResult(json, filters.from, filters.to);
};

// confirm every airport the passenger asked for on a legacy-app booking (sends SITA, tells the passenger)
// -> { request: LegacyBooking, alreadyConfirmed: boolean }
export const confirmLegacyAppBookingAirports = async (id) => {
  return request(`${legacy_url}/${encodeURIComponent(id)}/confirm-airports`, { method: "POST" });
};

// mark a legacy-app booking as rejected; it moves from Awaiting airport to Rejected
// -> { request: LegacyBooking, alreadyRejected: boolean }
export const rejectLegacyAppBooking = async (id) => {
  return request(`${legacy_url}/${encodeURIComponent(id)}/reject`, { method: "POST" });
};

// set or clear (empty string) the desk's internal note on a legacy-app booking; never shown to the passenger
// -> { request: LegacyBooking }
export const saveLegacyAppBookingNote = async (id, note) => {
  return request(`${legacy_url}/${encodeURIComponent(id)}/note`, {
    method: "PUT",
    body: JSON.stringify({ note }),
  });
};

// confirm one end of a request
// end: 'origin' | 'destination' | 'airline' (airline desk only ever sends 'airline')
// -> { request: DeskRequest, alreadyConfirmed: boolean }
export const confirmPaxRequest = async (id, end, { airline = false } = {}) => {
  return request(`${requestsBase(airline)}/${encodeURIComponent(id)}/confirm`, {
    method: "POST",
    body: JSON.stringify({ end }),
  });
};

// reason: one of the desk reject-reason ids, or empty when none was chosen
// -> { request: DeskRequest, alreadyRejected: boolean }
export const rejectPaxRequest = async (id, reason, { airline = false } = {}) => {
  return request(`${requestsBase(airline)}/${encodeURIComponent(id)}/reject`, {
    method: "POST",
    body: JSON.stringify(reason ? { reason } : {}),
  });
};

// Internal desk note. Empty string clears it. Never shown to the passenger.
// -> { request: DeskRequest }
export const savePaxRequestNote = async (id, note, { airline = false } = {}) => {
  return request(`${requestsBase(airline)}/${encodeURIComponent(id)}/note`, {
    method: "PUT",
    body: JSON.stringify({ note }),
  });
};
